// src/shared/components/DeliveryStopInput/DeliveryStopInput.jsx
import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Collapse,
  InputAdornment,
} from "@mui/material";
import { MapPin, Clock, FileText, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { LocationAutocomplete } from "../AutoComplete/LocationAutocomplete";

export const DeliveryStopInput = ({ index, stop, onChange, onRemove }) => {
  const [expanded, setExpanded] = useState(true);

  // Keep a local address string so the LocationAutocomplete
  // is not re-controlled by the parent on every keystroke.
  // We only push up to the parent when a suggestion is actually selected.
  const [localAddress, setLocalAddress] = useState(stop.address || "");

  const update = useCallback(
    (patch) => onChange({ ...stop, ...patch }),
    [stop, onChange]
  );

  const handleAddressChange = (value) => {
    // User is typing — update local state only, NOT the parent stop yet.
    // This prevents the parent re-render from fighting the input.
    setLocalAddress(value);
    // If they clear the field, propagate the clear.
    if (!value) update({ address: "", locationData: null });
  };

  const handleLocationSelect = (data) => {
    // A suggestion was picked — now push the final value up.
    setLocalAddress(data.address);
    update({ address: data.address, locationData: data });
  };

  const hasDetails = stop.time || stop.hint;

  const msToDateTimeLocal = (ms) => {
    if (!ms) return "";
    try {
      const d = new Date(ms);
      const offset = d.getTimezoneOffset() * 60000;
      return new Date(d - offset).toISOString().slice(0, 16);
    } catch {
      return "";
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* ── Address row ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, pl: 1.5 }}>
        {/* Stop number badge */}
        <Box
          sx={{
            minWidth: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.7rem",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {index + 2}
        </Box>

        <Box sx={{ flex: 1 }}>
          <LocationAutocomplete
            label={`Päämäärä ${index + 1}`}
            value={localAddress}
            onChange={handleAddressChange}
            onLocationSelect={handleLocationSelect}
            showDetectLocation={false}
            icon={MapPin}
          />
        </Box>

        <IconButton
          size="small"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? "Piilota tiedot" : "Lisää aika ja ohje"}
          sx={{ color: hasDetails ? "primary.main" : "text.secondary" }}
        >
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </IconButton>

        <IconButton size="small" onClick={onRemove} title="Poista pysäkki" sx={{ color: "error.main" }}>
          <Trash2 size={16} />
        </IconButton>
      </Box>

      {/* ── Expandable details ── */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            bgcolor: "action.hover",
          }}
        >
          <TextField
            size="small"
            fullWidth
            label="Toimitusaika"
            type="datetime-local"
            value={msToDateTimeLocal(stop.time)}
            onChange={(e) => {
              const ms = e.target.value ? new Date(e.target.value).getTime() : null;
              update({ time: ms });
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Clock size={16} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            size="small"
            fullWidth
            label="Toimitusohjeet"
            placeholder="esim. jätä ovelle, soita ensin..."
            value={stop.hint || ""}
            onChange={(e) => update({ hint: e.target.value })}
            multiline
            minRows={2}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                  <FileText size={16} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};